import type { RuntimeQuotaResponse } from './quotaRuntime.client';

type InflightEntry = {
  promise: Promise<RuntimeQuotaResponse>;
  signal?: AbortSignal;
};

const runtimeQuotaInflight = new Map<string, InflightEntry>();

/** Test-only: reset module-level dedupe between Vitest cases. */
export function __resetRuntimeQuotaInflightForTests(): void {
  runtimeQuotaInflight.clear();
}

function isReusable(entry: InflightEntry | undefined): entry is InflightEntry {
  return Boolean(entry && !entry.signal?.aborted);
}

/**
 * Dedupe GET /v1/tutor/runtime/quota by feature_name.
 * Shared across TeacherLive, useQuotaRuntimeV2, and sidebar callers.
 */
export function dedupeRuntimeQuotaFetch(
  featureName: string,
  run: () => Promise<RuntimeQuotaResponse>,
  opts?: { cacheBust?: boolean; signal?: AbortSignal },
): Promise<RuntimeQuotaResponse> {
  const key = featureName.trim() || '__default__';
  if (!opts?.cacheBust) {
    const existing = runtimeQuotaInflight.get(key);
    if (isReusable(existing)) {
      return existing.promise;
    }
  }

  const promise = run().finally(() => {
    if (!opts?.cacheBust) {
      const current = runtimeQuotaInflight.get(key);
      if (current?.promise === promise) {
        runtimeQuotaInflight.delete(key);
      }
    }
  });

  if (!opts?.cacheBust) {
    runtimeQuotaInflight.set(key, { promise, signal: opts?.signal });
  }
  return promise;
}
